import CDialog from '01_core/components/materials/CDialog';
import HintMark from '01_core/components/materials/HintMark';
import { withCallAPI } from '01_core/components/system/CallAPI';
import { CHECK_MAIL_ADDRESS, STATUS } from '01_core/constants/APIs';
import { LABEL } from '01_core/constants/labelConsts';
import { INFOBAR_VARIANT, TYPES_INFO_BAR, coreInfoBarActions } from '01_core/reducers/coreInfoBarActions';
import { Button, Paper, Typography, CssBaseline, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/HelpOutline';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import AboutSystem from './AboutSystem';
import { TYPES, loginActions } from '../loginActions';

import { Controller } from 'react-hook-form';
import { useRHFForm } from '01_core/validation/useRHFForm';
import { requiredString } from '01_core/validation/validationHelpers';

const FORM_MAX_WIDTH = 450;
const FORM_HEIGHT = 320;
const FORM_WIDTH = '95%';
const MARGIN_TOP_TEXTFIELD = 15;
const BUTTON_WRAP_MARGIN_TOP = 20;
const TITLE_MARGIN_BOTTOM = 30;

const Root = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'baseline',
  height: '100%',
  paddingTop: 'calc(50vh - 210px)',
});

const FormPaper = styled(Paper)({
  boxSizing: 'border-box',
  width: FORM_WIDTH,
  maxWidth: FORM_MAX_WIDTH,
  height: FORM_HEIGHT,
  padding: '10px 35px',
});

const IconBtnDiv = styled('div')({
  marginRight: -30,
  textAlign: 'right',
});

const StyledInfoIcon = styled(InfoIcon)(({ theme }) => ({
  color: theme.palette.icon.secondary,
  marginRight: 5,
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
  fontSize: '1.3rem',
  fontWeight: 700,
  marginBottom: TITLE_MARGIN_BOTTOM,
  color: theme.palette.secondary.main,
  whiteSpace: 'pre-wrap',
  '@media (max-width: 300px)': {
    fontSize: '1.2rem !important',
  },
}));

const LoginTypography = styled(Typography)({
  fontSize: '1.6rem',
});

const ButtonWrap = styled('div')({
  marginTop: BUTTON_WRAP_MARGIN_TOP,
  textAlign: 'right',
});

const PrimaryButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.etcButton.main,
  '&&:hover': {
    backgroundColor: '#ccc',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  boxSizing: 'border-box',
  width: '100%',
  marginTop: MARGIN_TOP_TEXTFIELD,
  '& > label': {
    color: '#999',
  },
  '& .MuiFormLabel-root': {
    color: '#999',
    zIndex: 1,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#999',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: '#999',
    borderBottomWidth: 1,
    transition: 'none',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: '#999',
    borderBottomWidth: 1,
  },
  '& .MuiInput-underline:after': {
    borderBottom: 'none',
    transition: 'none',
  },
  '& .MuiInput-underline.Mui-error:before': {
    borderBottomColor: theme.palette.error.main,
  },
  '& .MuiInput-underline.Mui-error:hover:not(.Mui-disabled):before': {
    borderBottomColor: theme.palette.error.main,
  },
  '& .MuiInput-underline.Mui-error:after': {
    borderBottom: 'none',
  },
}));

function SelfAuth(props) {
  const { intl, callAPI, setSelfAuth, openInfoBar } = props;

  const [isOpenPopup, setIsOpenPopup] = React.useState(false);

  // バリデーション（必須チェック）
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useRHFForm({
    defaultValues: { loginId: '' },
    shape: {
      loginId: requiredString(intl),
    },
  });

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));

  const isPc = () => {
    switch (true) {
      case isXs:
      case isSm:
        return false;
      default:
        return true;
    }
  };

  const onSubmit = async ({ loginId }) => {
    const trimmed = typeof loginId === 'string' ? loginId.trim() : '';
    try {
      const result = await callAPI(CHECK_MAIL_ADDRESS, { mailAddress: trimmed });
      if (result.status === STATUS.SUCCESS) {
        setSelfAuth({
          mailAddress: trimmed,
          exec: true,
        });
      } else {
        openInfoBar(INFOBAR_VARIANT.ERROR, result.message || intl.formatMessage({ id: LABEL.CORE_VALIDATE_MESSAGE }));
      }
    } catch (e) {
      openInfoBar(INFOBAR_VARIANT.ERROR, intl.formatMessage({ id: LABEL.CORE_SYSTEM_ERROR_MESSAGE }));
    }
  };

  const onInvalid = () => {
    openInfoBar(INFOBAR_VARIANT.ERROR, intl.formatMessage({ id: LABEL.CORE_VALIDATE_MESSAGE }));
  };

  return (
    <Root>
      <CssBaseline />
      <style dangerouslySetInnerHTML={{ __html: ['input::-ms-reveal {visibility:hidden}'] }} />

      <FormPaper>
        <form noValidate onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <IconBtnDiv>
            <Button onClick={() => { setIsOpenPopup(true); }} color='text.secondary'>
              <StyledInfoIcon />
              {intl.formatMessage({ id: LABEL.COMMON_SYSLOGIN_ABOUT_SYSTEM })}
            </Button>
          </IconBtnDiv>

          <TitleTypography align="center">
            {isPc()
              ? intl.formatMessage({ id: LABEL.COMMON_SYSLOGIN_SYSTEM_TITLE })
              : intl
                  .formatMessage({ id: LABEL.COMMON_SYSLOGIN_SYSTEM_TITLE })
                  .replace('One NEC ', 'One NEC\n')}
          </TitleTypography>

          <LoginTypography align="left">
            {intl.formatMessage({ id: LABEL.COMMON_SYSLOGIN_TITLE })}
            <HintMark hint={LABEL.COMMON_SYSLOGIN_HINT} />
          </LoginTypography>

          <Controller
            name="loginId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <StyledTextField
                id="selfauth-mail"
                type="text"
                label={intl.formatMessage({ id: LABEL.COMMON_SYSLOGIN_ID })}
                margin="normal"
                value={field.value ?? ''}
                variant="standard"
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
                name={field.name}
                inputRef={field.ref}
                error={!!errors.loginId}
                helperText={errors.loginId?.message}
              />
            )}
          />

          <ButtonWrap>
            <PrimaryButton
              type="submit"
              variant="contained"
            >
              {intl.formatMessage({ id: LABEL.COMMON_SYSLOGIN_LOGIN })}
            </PrimaryButton>
          </ButtonWrap>
        </form>
      </FormPaper>

      <CDialog
        open={isOpenPopup}
        handleClose={() => { setIsOpenPopup(false); }}
        title={intl.formatMessage({ id: LABEL.COMMON_SYSLOGIN_ABOUT_SYSTEM })}
        fullWidth={false}
        maxWidth={false}
      >
        <DialogContent>
          <AboutSystem />
        </DialogContent>
      </CDialog>
    </Root>
  );
}

const mapDispatchToProps = (dispatch) => ({
  openInfoBar: (type, message) => dispatch(coreInfoBarActions[TYPES_INFO_BAR.OPEN](type, message)),
  setSelfAuth: (selfAuth) => dispatch(loginActions[TYPES.SET_SELF_AUTH](selfAuth)),
});

export default connect(null, mapDispatchToProps)(withCallAPI(injectIntl(SelfAuth)));